import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { MLInsuredCandidatesComponent } from 'ml-components-common-react';
import MLPartialDriverDetailsComponent from "../MLPartialDriverDetailsComponent/MLPartialDriverDetailsComponent";

import metadata from './MLDriverChangeAddDriverPopup.metadata.json5';
import messages from './MLDriverChangeAddDriverPopup.messages';
import styles from './MLDriverChangeAddDriverPopup.module.scss';

const MLDriverChangeAddDriverPopup = (props) => {
    const {
        submissionVM,
        driverType,
        onAddDriver,
        onCancel,
        errorTimestamp,
        setErrorTimestamp,
        viewModelService,
        authHeader,
        showProgressDialog,
        EndorsementService
    } = props;

    const translator = useTranslator();
    const isAMP = appConfig.portalCode === 'AMP';
    const DRIVERS_PATH = 'lobData.personalAuto.coverables.drivers';
    const driversVM = _.get(submissionVM, DRIVERS_PATH, []);
    const driversVal = _.get(submissionVM, `${DRIVERS_PATH}.value`, {});
    const paCoverableVal = _.get(submissionVM, 'lobData.personalAuto.coverables.value', {});
    
    const [driver, setDriver] = useState(viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.DriverDTO'));
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [insuredCandidateSelected, setInsuredCandidateSelected] = useState(false);
    const [showErrorBannerMessage, setShowErrorBannerMessage] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState();
    const [savingModalData, setSavingModalData] = useState(false);

    const { onValidate } = useValidation('PAHouseholdMembersPagePolChange');
    const { refreshData } = useDataRefresh();

    const {
        checkUniqueSelf,
        checkUniqueSpouse,
    } = MLDriverVehicleUtil(viewModelService);

    const updateDriver = useCallback(() => {
        refreshData();
    }, [refreshData]);

    const addNewDriver = () => { 
        const newDriver = paCoverableVal.createDriver();
        newDriver.person.primaryAddress = submissionVM.baseData.policyAddress.value;
        newDriver.editMode_ML = true;
        newDriver.type_ML = driverType;
        newDriver.isAgentCreated = true;
        newDriver.accidents = '0';
        newDriver.violations = '0';
        newDriver.partialDriverAdditionFlag_ML = true;
        setDriver(driversVM.findFirstElement(newDriver));
    };

    useEffect(() => {
        addNewDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addDriver = () => {
        const isValid = checkUniqueSelf(driversVal) && checkUniqueSpouse(driversVal) && submissionVM.aspects.valid && submissionVM.aspects.subtreeValid;

        if (!insuredCandidateSelected) {
            setErrorBannerMessage(translator(messages.selectDriver));
            setShowErrorBannerMessage(true);
            return;
        }
        
        if (!isValid) {
            setShowValidationErrors(true);
            if(setErrorTimestamp && errorTimestamp !== undefined){
                setErrorTimestamp(errorTimestamp + 1);
            }
            return;
        }

        if (!savingModalData) {
            const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
            setSavingModalData(true);
            showProgressDialog(messages.saving, promise);
            promise.then((updatedPolicyChange) => {
                const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
                const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
                const existingDrivers = _.get(submissionVM, 'lobData.personalAuto.coverables.drivers.value');
                const updatedDrivers = _.get(updatedEndorsementVM, 'lobData.personalAuto.coverables.drivers.value');

                // Compare the existing drivers with those returned from PC to re-indentify the newly added driver and tag it as the selected driver.
                const fixedIds = [];
                existingDrivers.forEach((drv) => fixedIds.push(drv.fixedId));
                const index = updatedDrivers.findIndex((drv) => !fixedIds.includes(drv.fixedId));

                updatedDrivers[index].isSelected = true;
                updatedDrivers[index].isAgentCreated = true;
                updatedDrivers[index].editMode_ML = true;

                _.set(submissionVM, 'lobData.personalAuto.coverables.drivers.value', updatedDrivers);
                onAddDriver(onCancel);
            }).finally(() => {
                setSavingModalData(false);
            });
        }
    }

    const overrides = {
        contentContainer: {
            className: isAMP ? 'ml-add-driver-popup amp-form-padding' : 'ml-add-driver-popup'
        },
        insuredCandidatesComponent: {
            submissionVM,
            driverVM: driver,
            driverType,
            setInsuredCandidateSelected,
            partialDriverAdditionFlag: true,
            viewModelService
        },
        partialDriverDetailsComponent: {
            visible: insuredCandidateSelected,
            driver,
            checkUniqueSelf: () => checkUniqueSelf(driversVal),
            checkUniqueSpouse: () => checkUniqueSpouse(driversVal),
            onValidate,
            showValidationErrors,
            submissionVM,
            isExcluded: driverType === 'excluded',
            viewModelService
        },
        errorNotification: {
            visible: showErrorBannerMessage && !insuredCandidateSelected
        },
        validationErrorMessage: {
            content: errorBannerMessage
        },
        footer: {
            className: isAMP ? 'footer amp-footer-padding col-md-12' : 'footer col-md-12'
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlInsuredCandidatesComponent: MLInsuredCandidatesComponent,
            mlpartialdriverdetailscomponent: MLPartialDriverDetailsComponent
        },
        resolveCallbackMap : {
            addDriver,
            onCancel
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={driver}
            onModelChange={updateDriver}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValidationChange={onValidate}
            showErrors={showValidationErrors}
        />
    );
};

MLDriverChangeAddDriverPopup.propTypes = {
    submissionVM: PropTypes.object.isRequired,
    driverType: PropTypes.string.isRequired,
    onAddDriver: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default MLDriverChangeAddDriverPopup;
